import { IntegrationSpec } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { authnClient } from '@frontend/auth';
import { localStorageHelper } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { TableColumnConfig } from '@frontend/design-system';
import { useIntegrationsDashboardStore } from '../store';
import { Confirmation, UserRole, Verticals } from './constants';
import { RoleBasedValue, TableData } from './typings';

const CAPITAL_SPLIT_REGEX = /([a-z])([A-Z])/g; //  Regex to find boundaries between a lowercase and an uppercase letter to split strings.
const SEPARATORS_REGEX = /[-_.]+/g; //  Regex to match common separators like hyphens, underscores, or periods.
const MULTIPLE_SPACES_REGEX = /\s+/g; //  Regex to match one or more whitespace characters.

/**
 * Cache to avoid recomputing integration operation strings.
 * Key is `${dataType}_${operation}`, value is { formatted, camelCase }.
 */
const integrationOperationCache = new Map<string, { formatted: string; camelCase: string }>();

/**
 * Normalizes a string by splitting camelCase, replacing separators with spaces, and trimming whitespace.
 * @param {string} input The string to normalize.
 * @returns {string} The normalized, space-separated string.
 */
const normalizeString = (input: string): string =>
  (input || '')
    .replace(CAPITAL_SPLIT_REGEX, '$1 $2')
    .replace(SEPARATORS_REGEX, ' ')
    .replace(MULTIPLE_SPACES_REGEX, ' ')
    .trim();

/**
 * Formats a string into Title Case, where each word is capitalized.
 * @param {string} input The string to format.
 * @returns {string} The string in Title Case format.
 */
export const formatString = (input: string): string =>
  normalizeString(input)
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/**
 * Converts a string into camelCase format.
 * @param {string} input The string to convert.
 * @returns {string} The camelCased string.
 */
export const toCamelCase = (input: string): string =>
  normalizeString(input)
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (m, idx) => (idx === 0 ? m.toLowerCase() : m.toUpperCase()))
    .replace(MULTIPLE_SPACES_REGEX, '');

/**
 * Builds an integration operation label with both titleCase and camelCase forms.
 * Uses an internal cache to avoid recomputing labels for the same `dataType` + `operation` combination.
 *
 * Cache key format:
 *  - `${dataType}_${operation}` when operation is provided
 *  - `${dataType}` when operation is undefined
 *
 * @param dataType - The integration data type (e.g., 'user', 'deviceInfo').
 * @param operation - Optional operation name (e.g., 'create', 'read').
 * @returns An object containing:
 *  - formatted: Title-cased (e.g., "User Create")
 *  - camelCase: camelCased identifier (e.g., "userCreate")
 */
export const buildOperationLabel = (dataType: string, operation?: string): { formatted: string; camelCase: string } => {
  const key = operation ? `${dataType}_${operation}` : dataType;
  if (integrationOperationCache.has(key)) {
    return integrationOperationCache.get(key) as {
      formatted: string;
      camelCase: string;
    };
  }

  const combined = operation ? `${dataType} ${operation}` : dataType;
  const result = {
    formatted: formatString(combined),
    camelCase: toCamelCase(combined),
  };

  integrationOperationCache.set(key, result);
  return result;
};

/**
 * Replaces the last word in a header string based on the operation type.
 * - If the header ends with "List", it replaces it with "Sync".
 * - If the header ends with "Create" or "Update", it replaces it with "Writeback".
 * - Otherwise, the header is returned unchanged.
 *
 * @param {string} headerText - The input header string (e.g., "User List", "Account Create").
 * @returns {string} The modified header string with the appropriate replacement.
 */
const replaceHeaderText = (headerText: string): string => {
  const lastWord = headerText.split(' ').pop();
  const headerPrefix = headerText.substring(0, headerText.lastIndexOf(' '));

  switch (lastWord) {
    case 'List':
      return `${headerPrefix} Sync`;
    case 'Create':
    case 'Update':
      return `${headerPrefix} Writeback`;
    default:
      return headerText;
  }
};

/**
 * @constant typeColorMap
 * @description A map to associate integration connection types with specific colors for UI chips.
 */
const typeColorMap: Record<string, string> = {
  'Integration Type Unspecified': 'critical',
  'On Prem': 'warn',
  Cloud: 'primary',
  'File Upload': 'seaweed',
  'Manual Entry': 'indigo',
};

/**
 * Gets a color string based on the integration connection type.
 * @param {string} type The integration connection type (e.g., 'Cloud', 'On Premise').
 * @returns {string | undefined} The corresponding color string or undefined if not found.
 */
export const getTypeColor = (type: string): string | undefined => typeColorMap[buildOperationLabel(type).formatted];

/**
 * Formats a date into a clean, readable "Mon YYYY" format.
 * @param {string | number | Date} date The date to format.
 * @returns {string} The formatted date string (e.g., "Sep 2025").
 */
export const cleanDate = (date: string | number | Date): string =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
  });

/**
 * Collects a de-duplicated list of formatted operation labels from integration.
 * Iterates over the provided integrations and extracts operation labels from either
 * `syncMetadata` or `writebackMetadata`, depending on `metadataKey`.
 *
 * Each operation label is:
 *  1. Built using `buildOperationLabel` (dataType + operation)
 *  2. Normalized via `replaceHeaderText`
 *  3. Deduplicated using a Set
 *
 * @param integrations - List of integration specs returned from the API.
 * @param metadataKey - Metadata key to extract operations from ('syncMetadata' | 'writebackMetadata').
 * @returns A list of unique, formatted operation strings.
 */
const collectUniqueOperations = (
  integrations: IntegrationSpec[] | undefined,
  metadataKey: 'syncMetadata' | 'writebackMetadata'
): string[] => {
  const seenOperations = new Set<string>();

  for (const integration of integrations ?? []) {
    for (const metadata of integration[metadataKey] ?? []) {
      const formattedOperation = buildOperationLabel(metadata.dataType ?? '', metadata.operation ?? '').formatted;

      const operationInfo = replaceHeaderText(formattedOperation);
      seenOperations.add(operationInfo);
    }
  }

  return Array.from(seenOperations);
};

/**
 * Extracts unique Sync and Writeback operation labels from integrations.
 * Internally delegates to `collectUniqueOperations` to build separate,de-duplicated lists for:
 *  - Sync operations
 *  - Writeback operations
 *
 * @param integrations - list of integration specs.
 * @returns An object containing:
 *  - uniqueSyncOperations: Unique formatted sync operation labels
 *  - uniqueWritebackOperations: Unique formatted writeback operation labels
 */
export const getUniqueSyncWritebackMetadata = (
  integrations?: IntegrationSpec[]
): {
  uniqueSyncOperations: string[];
  uniqueWritebackOperations: string[];
} => {
  return {
    uniqueSyncOperations: collectUniqueOperations(integrations, 'syncMetadata'),
    uniqueWritebackOperations: collectUniqueOperations(integrations, 'writebackMetadata'),
  };
};

/**
 * Builds the integrations table's columns and data based on selected vertical.
 * @param {IntegrationSpec[]} integrations List of integrations.
 * @param {string} selectedVertical Currently selected vertical filter.
 * @returns {{ integrationsTableColumns: TableColumnConfig<TableData>[]; integrationsTableData: TableData[] }} Object containing table columns and data.
 */
export const buildIntegrationsTable = (
  integrations?: IntegrationSpec[],
  selectedVertical?: string
): { integrationsTableColumns: TableColumnConfig<TableData>[]; integrationsTableData: TableData[] } => {
  const normalizedSelectedVertical = selectedVertical?.toLowerCase();
  const isAllVertical = !normalizedSelectedVertical || normalizedSelectedVertical === Verticals.ALL.toLowerCase();

  // Defines the static columns present in the integrations table.
  const staticColumns: TableColumnConfig<TableData>[] = [
    {
      id: 'integration',
      Header: 'Integration',
      accessor: 'integration',
      minWidth: 150,
      sortType: 'string',
      filterConfig: { type: 'text' },
      sticky: 'left',
    },
    {
      id: 'tier',
      Header: 'Tier',
      accessor: 'tier',
      minWidth: 100,
      sortType: 'string',
      filterConfig: { type: 'radio' },
    },
    {
      id: 'type',
      Header: 'Type',
      accessor: 'type',
      minWidth: 150,
      sortType: 'string',
      filterConfig: { type: 'radio' },
      cellConfig: { element: 'Chip', variant: (type: string) => getTypeColor(type) },
    },
    {
      id: 'gaReleaseDate',
      Header: 'GA Date',
      accessor: 'gaReleaseDate',
      width: 150,
      sortType: 'date',
    },
    {
      id: 'lastUpdated',
      Header: 'Last Updated',
      accessor: 'lastUpdated',
      width: 150,
      sortType: 'date',
    },
  ];

  const { uniqueSyncOperations, uniqueWritebackOperations } = getUniqueSyncWritebackMetadata(integrations);

  // Defines dynamic columns based on unique sync and writeback operations.
  const dynamicColumns: TableColumnConfig<TableData>[] = [...uniqueSyncOperations, ...uniqueWritebackOperations]?.map(
    (operation) => {
      return {
        id: toCamelCase(operation),
        Header: operation,
        minWidth: 200,
        accessor: toCamelCase(operation),
        filterConfig: { type: 'radio' },
        cellRenderer: (value) => {
          return value === Confirmation.YES
            ? Icon({ name: 'confirm-badge', color: 'success' })
            : Icon({ name: 'error-badge', color: 'error' });
        },
      };
    }
  );

  // Combine static and dynamic columns for the final table column configuration.
  const integrationsTableColumns = [...staticColumns, ...dynamicColumns];

  // Maps known column ids to accessor functions to extract data from integrations.
  const predefinedAccessors: Record<string, (integration: IntegrationSpec) => string> = {
    integration: (integration) => (integration?.displayName ? integration?.displayName : ''),
    tier: (integration) =>
      integration?.tier && integration.tier !== 'TBD' ? buildOperationLabel(String(integration.tier)).formatted : '-',
    type: (integration) => (integration?.type ? buildOperationLabel(integration.type).formatted : ''),

    gaReleaseDate: (integration) => (integration?.gaReleaseDate ? cleanDate(integration?.gaReleaseDate) : ''),
    lastUpdated: (integration) => (integration?.gaReleaseDate ? cleanDate(integration?.gaReleaseDate) : ''), //TODO: Update this when last updated field available from api
  };

  /**
   * This accessor derives a normalized set of operation identifiers from both
   * `syncMetadata` and `writebackMetadata`. Each operation identifier is built by
   * combining `dataType` and `operation`, formatting the label, applying header
   * text replacements, and finally converting it to camelCase.
   *
   * The resulting identifiers are stored in a `Set` to allow constant-time
   * lookups when resolving multiple dynamic operation columns.
   *
   * @param integration - The integration specification containing sync and writeback metadata.
   * @param colId - Camel-cased operation column identifier to resolve.
   * @returns `YES` if the operation is supported by the integration; otherwise `NO`.
   */
  const defaultAccessor = (integration: IntegrationSpec, colId: string): Confirmation => {
    const operationsSet = new Set(
      [...(integration.syncMetadata ?? []), ...(integration.writebackMetadata ?? [])]
        .filter((op) => typeof op?.dataType === 'string' && typeof op?.operation === 'string')
        .map((op) =>
          toCamelCase(replaceHeaderText(buildOperationLabel(op.dataType as string, op.operation as string).formatted))
        )
    );

    return operationsSet.has(colId) ? Confirmation.YES : Confirmation.NO;
  };

  /**
   * Normalizes vertical values into a lowercase string array.
   *
   * Accepts either a single vertical or an array of verticals and ensures the result is always an array of lowercase strings.
   */
  const normalizeVerticals = (verticals?: string | string[]) => {
    if (!verticals) return [];
    return Array.isArray(verticals) ? verticals.map((v) => v.toLowerCase()) : [verticals.toLowerCase()];
  };

  /**
   * Filters integrations based on the selected vertical.
   *
   * If the "All" vertical is selected, all integrations are included.
   * Otherwise, only integrations whose verticals include the selected normalized vertical are retained.
   */
  const filteredIntegrations =
    integrations?.filter((integration) => {
      if (isAllVertical) return true;

      const integrationVerticals = normalizeVerticals(integration.verticals);
      return integrationVerticals.includes(normalizedSelectedVertical);
    }) ?? [];

  /**
   * Builds table row data for each filtered integration.
   *
   * For each column, a predefined accessor is used when available;
   * otherwise, the default accessor is applied to resolve the value.
   */
  const integrationsTableData: TableData[] = filteredIntegrations.map((integration) => {
    const rowData = {} as TableData;

    integrationsTableColumns.forEach((column) => {
      const accessor = predefinedAccessors[column.id];
      rowData[column.id] = accessor ? accessor(integration) : defaultAccessor(integration, column.id);
    });

    return rowData;
  });

  return { integrationsTableColumns, integrationsTableData };
};

/**
 * Checks whether the user is authenticated.
 *
 * The function verifies authentication by:
 * - Reading the `isUserAuthenticated` state from the integrations dashboard store and auth helper.
 * - Ensuring both `token` and `user` exist in localStorage.
 *
 * @returns {boolean} `true` if the user is authenticated, otherwise `false`.
 */
export const checkIsUserAuthenticated = (): boolean => {
  const { isUserAuthenticated } = useIntegrationsDashboardStore.getState();

  const token = localStorageHelper.get('token');
  const user = localStorageHelper.get('user');
  return isUserAuthenticated && !!token && !!user && authnClient.isUserAuthenticated();
};

/**
 * Evaluates RBAC (Role-Based Access Control) permissions for a section
 * and determines whether the current user has view and/or edit access.
 *
 * View access is granted if:
 * - `UserRole.ALL` is included in `accessibleByRoles`, or
 * - the user's role exists in `accessibleByRoles`, or
 * - the user's role is listed as an owner.
 *
 * Edit access is granted only if:
 * - the user's role is listed as an owner.
 *
 * @param {RoleBasedValue} [section] - Section configuration containing RBAC rules such as `accessibleByRoles` and `owners`.
 * @param {keyof typeof UserRole} [userRole] - The current user's role.
 *
 * @returns {{ hasViewAccess: boolean; hasEditAccess: boolean }}
 * An object indicating whether the user can view and/or edit the section.
 * If `section` or `userRole` is undefined, both permissions are `false`.
 */

export const checkRbacAccess = (
  section?: RoleBasedValue,
  userRole?: keyof typeof UserRole
): { hasViewAccess: boolean; hasEditAccess: boolean } => {
  if (!section || !userRole) return { hasViewAccess: false, hasEditAccess: false };

  const { accessibleByRoles = [], owners = [] } = section;
  const rolesSet = new Set(accessibleByRoles);
  const ownersSet = new Set(owners);

  const hasViewAccess = rolesSet.has(UserRole.ALL) || rolesSet.has(userRole) || ownersSet.has(userRole);
  const hasEditAccess = ownersSet.has(userRole);

  return { hasViewAccess, hasEditAccess };
};

/**
 * Converts a time string in minutes (e.g. "1660m") into
 * hours, days, or weeks depending on the value.
 *
 * @param {string} timeStr - Time string in minutes, e.g. "1660m"
 * @returns {string} - Converted time string (e.g. "27.7h", "1.2d", "2w")
 */
export const convertMinutes = (timeStr: string): string => {
  const minutes = parseFloat(timeStr);

  if (isNaN(minutes)) return '-';

  const minsInHour = 60;
  const hoursInDay = 24;
  const daysInWeek = 7;

  if (minutes < minsInHour) {
    return `${minutes}m`;
  }

  const hours = minutes / minsInHour;
  if (hours < hoursInDay) {
    return `${hours.toFixed(1)}h`;
  }

  const days = hours / hoursInDay;
  if (days < daysInWeek) {
    return `${days.toFixed(1)}d`;
  }

  const weeks = days / daysInWeek;
  return `${weeks.toFixed(1)}w`;
};

/**
 * Builds configuration and data for both "Entities" and "Writebacks" tables based on the provided integrations, and current integration context.
 *
 * This function processes the integration configuration to determine which entities
 * support read synchronization (Sync) and which support writeback (Writeback) operations.
 * It also attaches appropriate status icons and frequency information for display.
 *
 * @param {Integration[]} [integrations] - Optional list of integration configurations containing capabilities.
 * @param {string} [currentIntegration] - Optional identifier for the current integration (case and space insensitive).
 *
 * @returns {{
 *   entitiesTableColumns: TableColumnConfig<TableData>[];
 *   entitiesTableData: TableData[];
 *   writebacksTableColumns: TableColumnConfig<TableData>[];
 *   writebacksTableData: TableData[];
 * }} Returns column definitions and data for both the Entities and Writebacks tables.
 *
 */
export const buildEntitiesWritebacksTable = (
  integrations?: IntegrationSpec[],
  currentIntegration?: string
): {
  entitiesTableColumns: TableColumnConfig<TableData>[];
  entitiesTableData: TableData[];
  writebacksTableColumns: TableColumnConfig<TableData>[];
  writebacksTableData: TableData[];
} => {
  const normalizedCurrentIntegration = currentIntegration?.replace(/\s+/g, '').toLowerCase();

  const { integrationsTableData } = buildIntegrationsTable(integrations, Verticals.ALL);

  const currentIntegrationData =
    integrationsTableData?.find(
      (intz) => intz?.integration?.replace(/\s+/g, '').toLowerCase() === normalizedCurrentIntegration
    ) ?? {};

  const entries = Object.entries(currentIntegrationData);

  const entitiesTableData: TableData[] = [];
  const writebacksTableData: TableData[] = [];

  for (const [key, value] of entries) {
    const operation = buildOperationLabel(key).formatted;
    const label = replaceHeaderText(operation);
    const isConfirmed = value === Confirmation.YES;
    const icon = Icon({
      name: isConfirmed ? 'confirm-badge' : 'error-badge',
      color: isConfirmed ? 'success' : 'error',
    });

    if (label.endsWith('Sync')) {
      //const frequency = convertMinutes((matchedCap as any)?.syncTiming?.syncFrequencyInMinutes ?? '') ?? '-';

      entitiesTableData.push({
        entity: label,
        read: icon,
        value: isConfirmed ? Confirmation.YES : Confirmation.NO,
      });
    } else if (
      !label.endsWith('Integration') &&
      !label.endsWith('Type') &&
      !label.endsWith('Tier') &&
      !label.endsWith('Ga Release Date') &&
      !label.endsWith('Last Updated')
    ) {
      writebacksTableData.push({
        writeback: label,
        integrated: icon,
        value: isConfirmed ? Confirmation.YES : Confirmation.NO,
      });
    }
  }

  const entitiesTableColumns: TableColumnConfig<TableData>[] = [
    { id: 'entity', Header: 'Entity', accessor: 'entity' },
    { id: 'read', Header: 'Read', accessor: 'read' },
    //{ id: 'frequency', Header: 'Frequency', accessor: 'frequency' },
  ];

  const writebacksTableColumns: TableColumnConfig<TableData>[] = [
    { id: 'writeback', Header: 'Writeback', accessor: 'writeback' },
    { id: 'integrated', Header: 'Integrated', accessor: 'integrated' },
  ];

  return {
    entitiesTableColumns,
    entitiesTableData,
    writebacksTableColumns,
    writebacksTableData,
  };
};
